import { z } from 'zod';

// ============================================================================
// Date Range Schemas
// ============================================================================

/**
 * Schema for date range with validation
 */
export const DateRangeSchema = z.object({
  from: z.date(),
  to: z.date(),
}).refine(
  (data) => data.from <= data.to,
  {
    message: 'Start date must be before or equal to end date',
    path: ['from'],
  }
).refine(
  (data) => {
    const diffInDays = Math.ceil((data.to.getTime() - data.from.getTime()) / (1000 * 60 * 60 * 24));
    return diffInDays <= 31;
  },
  {
    message: 'Date range cannot exceed 31 days',
    path: ['to'],
  }
);

export type DateRange = z.infer<typeof DateRangeSchema>;

/**
 * Schema for date range with string dates (for API communication)
 */
export const DateRangeStringSchema = z.object({
  from: z.string().datetime(),
  to: z.string().datetime(),
}).refine(
  (data) => new Date(data.from) <= new Date(data.to),
  {
    message: 'Start date must be before or equal to end date',
    path: ['from'],
  }
).refine(
  (data) => {
    const fromDate = new Date(data.from);
    const toDate = new Date(data.to);
    const diffInDays = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
    return diffInDays <= 31;
  },
  {
    message: 'Date range cannot exceed 31 days',
    path: ['to'],
  }
);

export type DateRangeString = z.infer<typeof DateRangeStringSchema>;

/**
 * Schema for optional date range
 */
export const OptionalDateRangeSchema = DateRangeSchema.optional();

export type OptionalDateRange = z.infer<typeof OptionalDateRangeSchema>;

/**
 * Schema for optional date range with string dates
 */
export const OptionalDateRangeStringSchema = DateRangeStringSchema.optional();

export type OptionalDateRangeString = z.infer<typeof OptionalDateRangeStringSchema>;

// ============================================================================
// Date Range Preset Schemas
// ============================================================================

/**
 * Schema for predefined date range presets
 */
export const DateRangePresetSchema = z.enum([
  'today',
  'yesterday',
  'last_7_days',
  'last_14_days',
  'last_30_days',
  'this_month',
  'last_month',
  'custom',
]);

export type DateRangePreset = z.infer<typeof DateRangePresetSchema>;

/**
 * Schema for date range selection with preset
 */
export const DateRangeSelectionSchema = z.object({
  preset: DateRangePresetSchema,
  range: DateRangeSchema.optional(),
}).refine(
  (data) => {
    // If preset is 'custom', range must be provided
    if (data.preset === 'custom') {
      return data.range !== undefined;
    }
    return true;
  },
  {
    message: 'Custom date range must include from and to dates',
    path: ['range'],
  }
);

export type DateRangeSelection = z.infer<typeof DateRangeSelectionSchema>;

// ============================================================================
// Date Range Query Schemas
// ============================================================================

/**
 * Schema for date range query parameters
 */
export const DateRangeQueryParamsSchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  preset: DateRangePresetSchema.optional(),
}).refine(
  (data) => {
    // If from is provided, to must also be provided
    if (data.from && !data.to) {
      return false;
    }
    if (data.to && !data.from) {
      return false;
    }
    return true;
  },
  {
    message: 'Both from and to dates must be provided together',
  }
).refine(
  (data) => {
    // Validate date range if both dates are provided
    if (data.from && data.to) {
      const fromDate = new Date(data.from);
      const toDate = new Date(data.to);
      return fromDate <= toDate;
    }
    return true;
  },
  {
    message: 'Start date must be before or equal to end date',
  }
).refine(
  (data) => {
    // Validate max 31 days if both dates are provided
    if (data.from && data.to) {
      const fromDate = new Date(data.from);
      const toDate = new Date(data.to);
      const diffInDays = Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24));
      return diffInDays <= 31;
    }
    return true;
  },
  {
    message: 'Date range cannot exceed 31 days',
  }
);

export type DateRangeQueryParams = z.infer<typeof DateRangeQueryParamsSchema>;

// ============================================================================
// Date Range Utility Functions
// ============================================================================

/**
 * Convert DateRange to DateRangeString
 */
export function dateRangeToString(range: DateRange): DateRangeString {
  return {
    from: range.from.toISOString(),
    to: range.to.toISOString(),
  };
}

/**
 * Convert DateRangeString to DateRange
 */
export function dateRangeFromString(range: DateRangeString): DateRange {
  return {
    from: new Date(range.from),
    to: new Date(range.to),
  };
}

/**
 * Get date range for a preset
 */
export function getDateRangeForPreset(preset: DateRangePreset): DateRange | null {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (preset) {
    case 'today':
      return {
        from: today,
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1), // Start of next day
      };
    
    case 'yesterday':
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      return {
        from: yesterday,
        to: today, // Start of today (end of yesterday)
      };
    
    case 'last_7_days':
      return {
        from: new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000), // 6 days ago + today = 7 days
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1), // Start of next day
      };
    
    case 'last_14_days':
      return {
        from: new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000), // 13 days ago + today = 14 days
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1), // Start of next day
      };
    
    case 'last_30_days':
      return {
        from: new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000), // 29 days ago + today = 30 days
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1), // Start of next day
      };
    
    case 'this_month':
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        from: firstDayOfMonth,
        to: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1), // Start of next day
      };
    
    case 'last_month':
      const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const firstDayOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        from: firstDayOfLastMonth,
        to: firstDayOfThisMonth, // Start of this month (end of last month)
      };
    
    case 'custom':
      return null; // Custom ranges are handled separately
    
    default:
      return null;
  }
}

/**
 * Validate if a date range is within the maximum allowed days
 */
export function isValidDateRange(range: DateRange, maxDays: number = 31): boolean {
  const diffInDays = Math.ceil((range.to.getTime() - range.from.getTime()) / (1000 * 60 * 60 * 24));
  return diffInDays <= maxDays && range.from <= range.to;
}